import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { RequireAdmin } from "@/components/shared/require-admin";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { FormError } from "@/components/shared/form-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCollegeStrategicPillars,
  useCreateCollegeStrategicPillar,
  useUpdateCollegeStrategicPillar,
  useDeleteCollegeStrategicPillar,
  type CollegeStrategicPillar,
} from "@/hooks/use-college-strategic-pillars";
import {
  useCollegeGoals,
  useCreateCollegeGoal,
  useUpdateCollegeGoal,
  useDeleteCollegeGoal,
  type CollegeGoal,
} from "@/hooks/use-college-goals";

// ── Zod Schemas ──────────────────────────────────────────────

const pillarFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(5000).optional(),
  time_frame: z.string().max(255).optional(),
  sort_order: z.number().int().min(0).optional(),
});
type PillarFormValues = z.infer<typeof pillarFormSchema>;

const HORIZON_OPTIONS = [
  { value: "7_year", label: "7-Year" },
  { value: "3_year", label: "3-Year" },
  { value: "18_month", label: "18-Month" },
] as const;

const goalFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(5000).optional(),
  horizon: z.enum(["7_year", "3_year", "18_month"]),
  sort_order: z.number().int().min(0).optional(),
});
type GoalFormValues = z.infer<typeof goalFormSchema>;

// ── Horizon Badge ───────────────────────────────────────────

function HorizonBadge({ horizon }: { horizon: string }) {
  const label = HORIZON_OPTIONS.find((o) => o.value === horizon)?.label ?? horizon;
  return <Badge variant="secondary">{label}</Badge>;
}

// ── Skeleton ─────────────────────────────────────────────────

function PillarsTableSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading college strategy">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">Description</TableHead>
              <TableHead className="hidden md:table-cell">Time Frame</TableHead>
              <TableHead className="w-20">Order</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-64" /></TableCell>
                <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                <TableCell><Skeleton className="h-8 w-20" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Pillar Form Dialog ──────────────────────────────────────

function PillarFormDialog({
  open,
  onOpenChange,
  pillar,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pillar: CollegeStrategicPillar | null;
}) {
  const isEditing = pillar !== null;
  const createPillar = useCreateCollegeStrategicPillar();
  const updatePillar = useUpdateCollegeStrategicPillar(pillar?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PillarFormValues>({
    resolver: zodResolver(pillarFormSchema),
    defaultValues: getPillarDefaults(pillar),
  });

  useEffect(() => {
    reset(getPillarDefaults(pillar));
  }, [pillar, reset]);

  const isPending = createPillar.isPending || updatePillar.isPending;

  const onSubmit = (data: PillarFormValues) => {
    const body = {
      name: data.name,
      description: data.description || null,
      time_frame: data.time_frame || null,
      sort_order: data.sort_order ?? 0,
    };
    if (isEditing) {
      updatePillar.mutate(body, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    } else {
      createPillar.mutate(body, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit College Strategic Pillar" : "Create College Strategic Pillar"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the pillar details." : "Add a new college strategic pillar."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pillar-name">Name</Label>
            <Input id="pillar-name" placeholder='e.g., "Instruction"' {...register("name")} autoFocus />
            <FormError message={errors.name?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pillar-description">Description</Label>
            <Textarea id="pillar-description" placeholder="Describe the pillar..." rows={3} {...register("description")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pillar-time-frame">Time Frame</Label>
              <Input id="pillar-time-frame" placeholder='e.g., "October 2025 – June 2032"' {...register("time_frame")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pillar-sort-order">Sort Order</Label>
              <Input id="pillar-sort-order" type="number" {...register("sort_order", { valueAsNumber: true })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getPillarDefaults(pillar: CollegeStrategicPillar | null): PillarFormValues {
  return {
    name: pillar?.name ?? "",
    description: pillar?.description ?? "",
    time_frame: pillar?.time_frame ?? "",
    sort_order: pillar?.sort_order ?? 0,
  };
}

// ── Delete Pillar Dialog ────────────────────────────────────

// DeletePillarDialog replaced by DeleteConfirmDialog (inline at usage site)

// ── Goal Form Dialog ────────────────────────────────────────

function GoalFormDialog({
  open,
  onOpenChange,
  goal,
  collegeStrategicPillarId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: CollegeGoal | null;
  collegeStrategicPillarId: string;
}) {
  const isEditing = goal !== null;
  const createGoal = useCreateCollegeGoal();
  const updateGoal = useUpdateCollegeGoal(goal?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<GoalFormValues>({
    resolver: zodResolver(goalFormSchema),
    defaultValues: getGoalDefaults(goal),
  });

  useEffect(() => {
    reset(getGoalDefaults(goal));
  }, [goal, reset]);

  const isPending = createGoal.isPending || updateGoal.isPending;

  const onSubmit = (data: GoalFormValues) => {
    if (isEditing) {
      updateGoal.mutate(
        {
          name: data.name,
          description: data.description || null,
          horizon: data.horizon,
          sort_order: data.sort_order ?? 0,
        },
        { onSuccess: () => { onOpenChange(false); reset(); } },
      );
    } else {
      createGoal.mutate(
        {
          college_strategic_pillar_id: collegeStrategicPillarId,
          name: data.name,
          description: data.description || null,
          horizon: data.horizon,
          sort_order: data.sort_order ?? 0,
        },
        { onSuccess: () => { onOpenChange(false); reset(); } },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit College Objective" : "Create College Objective"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the college objective details." : "Add a new college objective to this pillar."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="goal-name">Name</Label>
            <Input id="goal-name" placeholder="College objective title..." {...register("name")} autoFocus />
            <FormError message={errors.name?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal-description">Description</Label>
            <Textarea id="goal-description" placeholder="Describe the goal..." rows={3} {...register("description")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Horizon</Label>
              <Controller
                name="horizon"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select horizon" />
                    </SelectTrigger>
                    <SelectContent>
                      {HORIZON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FormError message={errors.horizon?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal-sort-order">Sort Order</Label>
              <Input id="goal-sort-order" type="number" {...register("sort_order", { valueAsNumber: true })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getGoalDefaults(goal: CollegeGoal | null): GoalFormValues {
  return {
    name: goal?.name ?? "",
    description: goal?.description ?? "",
    horizon: goal?.horizon ?? "7_year",
    sort_order: goal?.sort_order ?? 0,
  };
}

// ── Delete Goal Dialog ──────────────────────────────────────

// DeleteGoalDialog replaced by DeleteConfirmDialog (inline at usage site)

// ── Goals Sub-Table ─────────────────────────────────────────

function GoalsSection({ collegeStrategicPillarId }: { collegeStrategicPillarId: string }) {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<CollegeGoal | null>(null);
  const deleteGoal = useDeleteCollegeGoal();

  const { data, isLoading } = useCollegeGoals({ college_strategic_pillar_id: collegeStrategicPillarId });
  const goals = data?.data ?? [];

  return (
    <div className="pl-8 pr-4 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">College Objectives</p>
        <Button size="sm" variant="outline" onClick={() => { setSelectedGoal(null); setFormOpen(true); }}>
          <Plus className="mr-1 h-3 w-3" /> New Goal
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : goals.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No college goals yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Horizon</TableHead>
              <TableHead className="hidden sm:table-cell">Description</TableHead>
              <TableHead className="w-20">Order</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {goals.map((goal) => (
              <TableRow key={goal.id}>
                <TableCell className="text-sm font-medium">{goal.name}</TableCell>
                <TableCell><HorizonBadge horizon={goal.horizon} /></TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-[hsl(var(--muted-foreground))] truncate max-w-xs">
                  {goal.description ?? "—"}
                </TableCell>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{goal.sort_order}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedGoal(goal); setFormOpen(true); }} aria-label={`Edit goal ${goal.name}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedGoal(goal); setDeleteOpen(true); }} aria-label={`Delete goal ${goal.name}`}>
                      <Trash2 className="h-3 w-3 text-[hsl(var(--destructive))]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <GoalFormDialog open={formOpen} onOpenChange={setFormOpen} goal={selectedGoal} collegeStrategicPillarId={collegeStrategicPillarId} />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete College Objective"
        description={<>Are you sure you want to delete <strong>{selectedGoal?.name}</strong>? This will fail if focus areas still reference this goal.</>}
        onConfirm={() => { if (selectedGoal) deleteGoal.mutate(selectedGoal.id, { onSuccess: () => setDeleteOpen(false) }); }}
        isPending={deleteGoal.isPending}
      />
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────

export function AdminCollegeStrategyPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState<CollegeStrategicPillar | null>(null);
  const deletePillar = useDeleteCollegeStrategicPillar();
  const [expandedPillarIds, setExpandedPillarIds] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useCollegeStrategicPillars();
  const pillars = data?.data ?? [];

  const togglePillar = (id: string) => {
    setExpandedPillarIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <RequireAdmin>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-heading text-2xl font-semibold sm:text-3xl">College Strategy</h1>
          <Button onClick={() => { setSelectedPillar(null); setFormOpen(true); }} aria-label="Create new college strategic pillar">
            <Plus className="mr-2 h-4 w-4" />
            New Pillar
          </Button>
        </div>

        {isLoading ? (
          <PillarsTableSkeleton />
        ) : error ? (
          <EmptyState title="Failed to load college strategy" description={error.message} />
        ) : pillars.length === 0 ? (
          <EmptyState
            title="No college strategic pillars found"
            description="Create your first college strategic pillar to get started."
            actionLabel="New Pillar"
            onAction={() => { setSelectedPillar(null); setFormOpen(true); }}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Description</TableHead>
                  <TableHead className="hidden md:table-cell">Time Frame</TableHead>
                  <TableHead className="w-20">Order</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pillars.map((pillar) => (
                  <PillarRow
                    key={pillar.id}
                    pillar={pillar}
                    expanded={expandedPillarIds.has(pillar.id)}
                    onToggle={() => togglePillar(pillar.id)}
                    onEdit={() => { setSelectedPillar(pillar); setFormOpen(true); }}
                    onDelete={() => { setSelectedPillar(pillar); setDeleteOpen(true); }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <PillarFormDialog open={formOpen} onOpenChange={setFormOpen} pillar={selectedPillar} />
        <DeleteConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete College Strategic Pillar"
          description={<>Are you sure you want to delete <strong>{selectedPillar?.name}</strong>? This will fail if the pillar still has college goals. Delete the goals first.</>}
          onConfirm={() => { if (selectedPillar) deletePillar.mutate(selectedPillar.id, { onSuccess: () => setDeleteOpen(false) }); }}
          isPending={deletePillar.isPending}
        />
      </div>
    </RequireAdmin>
  );
}

function PillarRow({
  pillar,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  pillar: CollegeStrategicPillar;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <TableRow>
        <TableCell>
          <Button variant="ghost" size="sm" onClick={onToggle} aria-label={expanded ? "Collapse goals" : "Expand goals"}>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">{pillar.name}</TableCell>
        <TableCell className="hidden sm:table-cell text-[hsl(var(--muted-foreground))] truncate max-w-xs">
          {pillar.description ?? "—"}
        </TableCell>
        <TableCell className="hidden md:table-cell text-[hsl(var(--muted-foreground))]">
          {pillar.time_frame ?? "—"}
        </TableCell>
        <TableCell className="text-[hsl(var(--muted-foreground))]">{pillar.sort_order}</TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${pillar.name}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete ${pillar.name}`}>
              <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="p-0 border-b">
            <GoalsSection collegeStrategicPillarId={pillar.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
